import { useState } from 'react';

import { Banner, Box, Button, Dialog, H1, Text } from '@betterment/samba-react';
import * as Sentry from '@sentry/browser';

import { useCancelTransaction } from './useCancelTransaction';

import { trackClick } from '../../../../features/track';

interface CancelOrderButtonProps {
  transactionId: string;
}

export const CancelOrderButton: React.FC<CancelOrderButtonProps> = ({ transactionId }) => {
  const { cancelTransaction, loading, error } = useCancelTransaction();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCancel = async () => {
    try {
      const mutationResult = await cancelTransaction({ variables: { id: transactionId } });
      if (!mutationResult.errors) setIsModalOpen(false);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen} overlay="default">
      <Dialog.Trigger asChild={true}>
        <Box css={{ py: 'space.200' }} onClick={() => trackClick({ name: 'ActivityCancelorder' })}>
          <Button variant="textCritical" disabled={loading}>
            <Text variant="text100">Cancel order</Text>
          </Button>
        </Box>
      </Dialog.Trigger>
      <Dialog.Content
        size="large"
        css={{
          display: 'flex',
          flexDirection: 'column',
          p: 'space.400',
          gap: 'space.400',
          zIndex: 9999,
        }}
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Are you sure you want to cancel?</H1>
        </Dialog.Title>
        {error && (
          <Banner variant="critical">
            <Banner.Content>
              <Text as="p">Something went wrong.</Text>
            </Banner.Content>
          </Banner>
        )}
        <Box css={{ display: 'flex', gap: 'space.200' }}>
          <Button onClick={handleCancel} variant="solidPrimary" disabled={loading}>
            <Text variant="text100">Yes, I'm sure</Text>
          </Button>

          <Dialog.Close asChild={true}>
            <Button variant="solidSecondary">Nevermind</Button>
          </Dialog.Close>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
